import * as React from 'react';
import type { VerduStats } from '../../types/verdu-landing';

interface SocialProofProps {
  stats: VerduStats;
}

function formatNumber(n: number): string {
  if (n >= 1000) {
    return n.toLocaleString('es-AR');
  }
  return String(n);
}

export function SocialProof({ stats }: SocialProofProps) {
  if (stats.totalOrders === 0 && stats.topProducts.length === 0) {
    return null;
  }

  return (
    <section className="verdu-social-proof">
      <style>{`
        .verdu-social-proof {
          padding: 64px 24px;
          background: #fafaf7;
        }
        .verdu-social-inner {
          max-width: 960px;
          margin: 0 auto;
        }
        .verdu-social-proof h2 {
          text-align: center;
          font-size: 26px;
          font-weight: 700;
          color: #1a1a1a;
          margin: 0 0 12px;
        }
        .verdu-social-proof .section-sub {
          text-align: center;
          font-size: 16px;
          color: #666;
          margin: 0 0 40px;
        }
        .verdu-stats-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
          margin-bottom: 40px;
        }
        .verdu-stat-card {
          text-align: center;
          padding: 24px 16px;
          background: #fff;
          border-radius: 12px;
          border: 1px solid #eee;
        }
        .verdu-stat-number {
          font-size: 32px;
          font-weight: 800;
          color: #1EAA25;
          line-height: 1;
          margin-bottom: 6px;
        }
        .verdu-stat-label {
          font-size: 14px;
          color: #666;
        }
        .verdu-top-products {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
        }
        .verdu-top-product {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px 16px;
          background: #fff;
          border-radius: 10px;
          border: 1px solid #eee;
        }
        .verdu-top-product img {
          width: 48px;
          height: 48px;
          border-radius: 8px;
          object-fit: cover;
          flex-shrink: 0;
        }
        .verdu-top-product-info {
          min-width: 0;
        }
        .verdu-top-product-name {
          font-size: 14px;
          font-weight: 600;
          color: #333;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .verdu-top-product-stat {
          font-size: 13px;
          color: #1EAA25;
          font-weight: 500;
        }
        @media (max-width: 768px) {
          .verdu-stats-grid {
            grid-template-columns: repeat(3, 1fr);
            gap: 12px;
          }
          .verdu-stat-number {
            font-size: 24px;
          }
          .verdu-top-products {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
      <div className="verdu-social-inner">
        <h2>Lo que movemos para verdulerías</h2>
        <p className="section-sub">Datos reales de los últimos 30 días.</p>

        <div className="verdu-stats-grid">
          <div className="verdu-stat-card">
            <div className="verdu-stat-number">{formatNumber(stats.totalKilos)}</div>
            <div className="verdu-stat-label">kilos entregados</div>
          </div>
          <div className="verdu-stat-card">
            <div className="verdu-stat-number">{formatNumber(stats.totalOrders)}</div>
            <div className="verdu-stat-label">pedidos a verdulerías</div>
          </div>
          <div className="verdu-stat-card">
            <div className="verdu-stat-number">{formatNumber(stats.totalCompanies)}</div>
            <div className="verdu-stat-label">verdulerías abastecidas</div>
          </div>
        </div>

        {stats.topProducts.length > 0 && (
          <div className="verdu-top-products">
            {stats.topProducts.map((p) => (
              <div key={p.product_id} className="verdu-top-product">
                {p.image_url && (
                  <img src={p.image_url} alt={p.product_name} loading="lazy" />
                )}
                <div className="verdu-top-product-info">
                  <div className="verdu-top-product-name">{p.product_name}</div>
                  <div className="verdu-top-product-stat">
                    {formatNumber(p.total_units)} {p.unit_short_name}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
